import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { hasNativeShareSupport, triggerNativeShareDialog } from '../../utils/nativeShare';
import useOutsideClick from '../../hooks/useOutsideClick';
// import PropTypes from 'prop-types';
import styles from './Modal.module.scss';
import ButtonSecondaryEllipse from '../ButtonSecondaryEllipse';
import { Dialog, DialogDismiss, DialogHeading, useDialogState } from 'ariakit/dialog';
import ButtonPrimary from '../ButtonPrimary';
import { trackSharePlatform } from '../../utils/dataLayer';

const Modal = ({ title = '', url = '', onClose = () => {}, isVisible }) => {
    const ref = useRef(null);
    const modalClasses = classNames(styles['Modal'], 'black-glass', {});
    const rootClasses = classNames(styles['Root'], 'grid', {});
    const [shareLink, setShareLink] = useState('');
    const [message, setMessage] = useState(null);
    const { t } = useTranslation();
    useOutsideClick(ref, () => onClose());
    const dialog = useDialogState({
        animated: true,
    });

    const handleOpenModal = (isVisible) => {
        if (isVisible === true) {
            dialog.show();

            let shareUrl = url;
            if (!shareUrl.startsWith('http')) {
                shareUrl = url ? document.location.origin + url : window.location;
            }
            if (hasNativeShareSupport()) {
                return triggerNativeShareDialog(t('ShareModal.labels.share'), shareUrl);
            }

            setShareLink(shareUrl);
        }
    };
    useEffect(() => {
        handleOpenModal(isVisible);
    }, [isVisible]);

    const getShareLinkSM = () => {
        const encodedUrl = encodeURIComponent(shareLink);
        return {
            facebook: {
                title: title,
                text: t('ShareModal.labels.facebook'),
                url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&amp;src=sdkpreparse`,
            },
            mail: `mailto: ?subject=:&body=${'Your friend shared this page: ' + shareLink}`,
            linkedIn: {
                title: title,
                text: t('ShareModal.labels.linkedin'),
                url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
            },
        };
    };

    const closeModal = () => {
        setTimeout(() => {
            dialog.hide();
            onClose();
        }, 500);
    };

    const shareLinkFb = () => {
        trackSharePlatform('facebook');
        window.open(getShareLinkSM().facebook.url);
    };

    const shareLinkLIN = () => {
        trackSharePlatform('linkedin');
        window.open(getShareLinkSM().linkedIn.url);
    };
    const copyToClipboard = () => {
        trackSharePlatform('copy-to-clipboard');
        try {
            navigator.clipboard.writeText(shareLink);
            setMessage(t('ShareModal.actions.copyMessage'));
        } catch (err) {
            setMessage(t('ShareModal.actions.copyError'));
        }
    };
    return (
        <>
            <Dialog backdropProps={{ className: rootClasses }} state={dialog} className={modalClasses} ref={ref}>
                <>
                    <DialogHeading className={styles['DialogHeader']}>{t('ShareModal.dialog.headline')}</DialogHeading>
                    <div className={`${styles['ButtonContainer']}`}>
                        <ButtonSecondaryEllipse
                            icon={'icon-email'}
                            modifiers={['NoPadding']}
                            asLink={true}
                            onClick={() => {
                                trackSharePlatform('mail');
                            }}
                            href={getShareLinkSM().mail}>
                            <span className="sr-only">{t('ShareModal.labels.email')}</span>
                        </ButtonSecondaryEllipse>
                        <ButtonSecondaryEllipse icon={'icon-facebook'} modifiers={['NoPadding']} onClick={shareLinkFb}>
                            <span className="sr-only">{t('ShareModal.labels.facebook')}</span>
                        </ButtonSecondaryEllipse>
                        <ButtonSecondaryEllipse
                            icon={'icon-linkedin-white'}
                            modifiers={['NoPadding']}
                            onClick={shareLinkLIN}>
                            <span className="sr-only">{t('ShareModal.labels.linkedin')}</span>
                        </ButtonSecondaryEllipse>
                    </div>
                    <div className={styles['InputContainer']}>
                        <input
                            id="shareLink"
                            className={styles['Input']}
                            autoComplete="off"
                            maxLength={150}
                            value={shareLink}
                            readOnly
                        />
                    </div>
                    <div className={styles['CopyButtonContainer']}>
                        <ButtonPrimary
                            icon={'icon-copy-white'}
                            size={'small'}
                            modifiers={['PurpleBlue', 'InlineFlex']}
                            onClick={copyToClipboard}>
                            {t('ShareModal.labels.copy')}
                        </ButtonPrimary>
                    </div>

                    <DialogDismiss className={styles['Dismiss']} onClick={onClose}>
                        <span className="sr-only">{t('ShareModal.dialog.button')}</span>
                    </DialogDismiss>
                </>

                <div className={styles['Message']}>{message && <p>{message}</p>}</div>
            </Dialog>
        </>
    );
};
Modal.propTypes = {};

export default Modal;
